import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../lib/api';
import {
  MagnifyingGlass, Users, ShieldCheck, Key, Cube, Browser,
  ArrowRight, Command, X, SpinnerGap, User
} from '@phosphor-icons/react';

const TYPE_CONFIG = {
  page:        { icon: Browser,     color: 'text-navy-500',   bg: 'bg-navy-50',    label: 'Pages' },
  identity:    { icon: User,        color: 'text-blue-600',   bg: 'bg-blue-50',    label: 'Identities' },
  role:        { icon: ShieldCheck, color: 'text-purple-600', bg: 'bg-purple-50',  label: 'Roles' },
  entitlement: { icon: Key,         color: 'text-amber-600',  bg: 'bg-amber-50',   label: 'Entitlements' },
  application: { icon: Cube,        color: 'text-orange-600', bg: 'bg-orange-50',  label: 'Applications' },
};

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Cmd+K / Ctrl+K listener + custom open event
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    const openHandler = () => setOpen(true);
    window.addEventListener('keydown', handler);
    window.addEventListener('open-global-search', openHandler);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('open-global-search', openHandler);
    };
  }, [open]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced search
  const doSearch = useCallback(async (q) => {
    if (!q.trim()) { setResults([]); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await API.get('/search/global', { params: { q: q.trim() } });
      setResults(res.data.results || []);
      setActiveIndex(0);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(() => doSearch(query), 150);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, doSearch]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[activeIndex]) {
      e.preventDefault();
      selectResult(results[activeIndex]);
    }
  };

  const selectResult = (item) => {
    setOpen(false);
    navigate(item.path);
  };

  // Group results by type
  const grouped = {};
  results.forEach(r => {
    if (!grouped[r.type]) grouped[r.type] = [];
    grouped[r.type].push(r);
  });
  const groupOrder = ['page', 'identity', 'role', 'entitlement', 'application'];
  let flatIndex = 0;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="global-search-overlay">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-fade-in"
        onClick={() => setOpen(false)}
      />

      {/* Dialog */}
      <div className="relative flex justify-center pt-[15vh]">
        <div
          className="w-full max-w-xl bg-white rounded-lg shadow-2xl border border-slate-200 overflow-hidden animate-scale-in"
          data-testid="global-search-dialog"
        >
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 h-14 border-b border-slate-200">
            <MagnifyingGlass size={20} className="text-navy-400 shrink-0" />
            <input
              ref={inputRef}
              data-testid="global-search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search identities, roles, pages..."
              className="flex-1 text-sm text-navy-900 placeholder:text-navy-400 outline-none bg-transparent"
              autoComplete="off"
            />
            {loading && <SpinnerGap size={16} className="animate-spin text-orange-500 shrink-0" />}
            <kbd className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-[10px] text-navy-400 font-mono shrink-0">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div className="max-h-[50vh] overflow-y-auto" data-testid="global-search-results">
            {query.trim() && !loading && results.length === 0 && (
              <div className="px-4 py-10 text-center">
                <MagnifyingGlass size={28} className="mx-auto mb-2 text-navy-300" />
                <p className="text-sm text-navy-500">No results for "{query}"</p>
                <p className="text-xs text-navy-400 mt-1">Try a different search term</p>
              </div>
            )}

            {!query.trim() && (
              <div className="px-4 py-6 text-center">
                <p className="text-xs text-navy-400">Type to search across identities, roles, entitlements, applications, and pages</p>
              </div>
            )}

            {groupOrder.map(type => {
              const items = grouped[type];
              if (!items || items.length === 0) return null;
              const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.page;
              return (
                <div key={type}>
                  <div className="px-4 pt-3 pb-1">
                    <p className="text-[10px] font-bold text-navy-400 uppercase tracking-widest">{cfg.label}</p>
                  </div>
                  {items.map((item) => {
                    const idx = flatIndex++;
                    const Icon = cfg.icon;
                    const isActive = idx === activeIndex;
                    return (
                      <button
                        key={`${item.type}-${item.id}`}
                        data-testid={`search-result-${item.type}-${item.id}`}
                        onClick={() => selectResult(item)}
                        onMouseEnter={() => setActiveIndex(idx)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                          isActive ? 'bg-orange-50' : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${isActive ? 'bg-orange-100' : cfg.bg}`}>
                          <Icon size={16} weight="duotone" className={isActive ? 'text-orange-600' : cfg.color} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm truncate ${isActive ? 'text-orange-900 font-semibold' : 'text-navy-900 font-medium'}`}>
                            {item.name}
                          </p>
                          {item.detail && (
                            <p className="text-[11px] text-navy-400 truncate">{item.detail}</p>
                          )}
                        </div>
                        {isActive && (
                          <ArrowRight size={14} className="text-orange-500 shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
            {/* Reset flatIndex for re-render consistency */}
            <span className="hidden">{flatIndex = 0}</span>
          </div>

          {/* Footer */}
          {results.length > 0 && (
            <div className="px-4 py-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-navy-400">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 rounded border border-slate-200 bg-slate-50 font-mono">↑↓</kbd> navigate</span>
                <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 rounded border border-slate-200 bg-slate-50 font-mono">↵</kbd> select</span>
                <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 rounded border border-slate-200 bg-slate-50 font-mono">esc</kbd> close</span>
              </div>
              <span>{results.length} result{results.length !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
